import { useCallback, useEffect, useState } from 'react';
import { bytes, when } from '../../lib/n4';
import { type CheckRow, type Manifest, type ReleaseDiff, type Site, checkRows, errText, manifestOf, noteOf, pickList, post, releaseDiffOf, siteApi, toMs } from './common';
import { ChecksTable, DeployPanel, resultCall } from './DeployPanel';

// A szerver a release-mappa nevét adja id-ként (pl. 20261013-0713); a sorrendet itt rendezzük
interface Release { id: number | string; dir?: string; note?: string | null; by?: string | null; created_at?: number | string; current?: boolean; manifest: Manifest | null; diff: ReleaseDiff | null }

const signed = (n: number) => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : '±0');
const signedBytes = (n: number) => (n > 0 ? `+${bytes(n)}` : n < 0 ? `−${bytes(-n)}` : '±0');
// Eltérés az előzőhöz: a szerver diff-je (új/változott/törölt), különben a manifestek különbsége (fájlszám, méret)
function DiffCell({ r, prev }: { r: Release; prev: Release | undefined }) {
  const d = r.diff;
  if (d && (d.added != null || d.changed != null || d.removed != null)) return <span className="a-site-chsum"><b className="add">+{d.added ?? 0}</b> <b className="chg">~{d.changed ?? 0}</b> <b className="del">−{d.removed ?? 0}</b>{d.bytes != null && <span> · {signedBytes(d.bytes)}</span>}</span>;
  const df = d?.files ?? (r.manifest?.files != null && prev?.manifest?.files != null ? r.manifest.files - prev.manifest.files : null);
  const db = d?.bytes ?? (r.manifest?.bytes != null && prev?.manifest?.bytes != null ? r.manifest.bytes - prev.manifest.bytes : null);
  if (df == null && db == null) return <span className="mut">—</span>;
  const same = r.manifest?.sha1 && prev?.manifest?.sha1 && r.manifest.sha1 === prev.manifest.sha1;
  return <span className="a-site-chsum">{same ? <span className="mut">azonos</span> : <>{df != null && <b className={df > 0 ? 'add' : df < 0 ? 'del' : ''}>{signed(df)} fájl</b>}{db != null && <span> · {signedBytes(db)}</span>}</>}</span>;
}

// Kiadások fül: „Kiadás innen” (száraz próba / kiadás), „Ellenőrzés most”, a kiadások listája manifesttel, „Visszaállítás erre”.
export function ReleasesList({ site, onChanged }: { site: Site; onChanged: () => void }) {
  const [list, setList] = useState<Release[] | null>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState<number | string | null>(null);
  const [verify, setVerify] = useState<{ busy: boolean; rows: CheckRow[] | null; note: string; err: string } | null>(null);
  const load = useCallback(() => {
    siteApi<unknown>(site.id, '/releases').then((r) => {
      const rows = pickList<Record<string, unknown>>(r, 'releases').map((x) => ({ ...(x as unknown as Release), manifest: manifestOf(x), diff: releaseDiffOf(x) }));
      setList(rows.sort((a, b) => (toMs(b.created_at) ?? 0) - (toMs(a.created_at) ?? 0))); setErr('');
    }).catch((e) => setErr(errText(e, 'Kiadások')));
  }, [site.id]);
  useEffect(() => { load(); }, [load]);

  const rollback = async (r: Release) => {
    if (!confirm(`Visszaállítod a weboldalt erre a kiadásra?\n\n${new Date(toMs(r.created_at) ?? 0).toLocaleString('hu-HU')}${r.note ? ` — ${r.note}` : ''}\n\n${site.kind === 'static' ? 'A „current” mutató átáll (azonnali, kiesés nélkül).' : 'A szolgáltatás újraindul.'}`)) return;
    setBusy(r.id); setErr('');
    try { const res = await siteApi(site.id, '/rollback', post({ release_id: r.id })); setMsg(noteOf(res) || 'Visszaállítva.'); load(); onChanged(); }
    catch (e) { setErr(errText(e, 'Visszaállítás')); } finally { setBusy(null); }
  };
  // Ellenőrzés most: a kiadás utáni ellenőrzés külön (health_paths + required_headers + smoke), rollback nélkül
  const runVerify = async () => {
    setVerify({ busy: true, rows: null, note: '', err: '' });
    try { const r = await resultCall(site.id, '/verify', {}); setVerify({ busy: false, rows: checkRows(r), note: noteOf(r), err: '' }); }
    catch (e) { setVerify({ busy: false, rows: null, note: '', err: errText(e, 'Ellenőrzés') }); }
  };
  // A legfrissebb a jelenlegi, ha a szerver nem jelöli külön
  const currentId = list?.find((r) => r.current)?.id ?? list?.[0]?.id;
  const hasManifest = Boolean(list?.some((r) => r.manifest));

  return (
    <div>
      <DeployPanel site={site} onDeployed={() => { load(); onChanged(); }} />

      <div className="a-site-bar a-site-relbar">
        <h3 className="a-site-h3">Kiadások</h3>
        <span className="flex-1" />
        <button type="button" className="a-btn sm" disabled={verify?.busy} onClick={() => void runVerify()} title="Health-útvonalak, kötelező fejlécek és smoke a valódi címen — visszaállítás nélkül">{verify?.busy ? 'Ellenőrzés…' : 'Ellenőrzés most'}</button>
      </div>
      {verify && !verify.busy && (
        <div className={`a-site-result ${verify.err || verify.rows?.some((c) => c.ok === false) ? 'bad' : ''}`}>
          <div className="h"><b>Ellenőrzés</b>{verify.note && <span>{verify.note}</span>}<button type="button" onClick={() => setVerify(null)} title="Bezárás">×</button></div>
          {verify.err ? <div className="a-site-msg bad">{verify.err}</div> : <ChecksTable rows={verify.rows ?? []} />}
        </div>
      )}
      <p className="a-site-note">{site.kind === 'static' ? 'Statikus oldalnál minden kiadás külön mappa (www/releases/…); a visszaállítás csak a „current” mutatót állítja át. Az utolsó 5 marad meg.' : 'Proxy és konténer oldalnál a kiadás = újraindítás az új kóddal; a visszaállítás a korábbi állapotot indítja.'} Az ügynök az n4_site deploy-jal ad ki.</p>
      {err && <div className="a-site-msg bad">{err}</div>}
      {msg && <div className="a-site-msg ok">{msg}</div>}
      {list && (
        <div className="a-site-tablewrap">
          <table className="a-site-table a-site-rels">
            <thead><tr><th>Idő</th><th>Megjegyzés</th><th>Ki</th>{hasManifest && <th className="num">Fájlok / méret</th>}{hasManifest && <th>Eltérés az előzőhöz</th>}<th className="acts" /></tr></thead>
            <tbody>
              {list.map((r, i) => (
                <tr key={r.id} className={r.id === currentId ? 'cur' : ''}>
                  <td className="tm">{when(toMs(r.created_at))}{r.id === currentId && <span className="a-site-curtag">jelenlegi</span>}</td>
                  <td title={r.dir ?? String(r.id)}>{r.note || <span className="mut">—</span>}<div className="sub mono">{r.dir ? r.dir.split('/').slice(-2).join('/') : String(r.id)}</div></td>
                  <td className="mut">{r.by || '—'}</td>
                  {hasManifest && <td className="num" title={r.manifest?.sha1 ? `sha1 ${r.manifest.sha1}` : undefined}>{r.manifest ? <>{r.manifest.files ?? '—'} fájl<div className="sub">{r.manifest.bytes != null ? bytes(r.manifest.bytes) : ''}</div></> : <span className="mut">—</span>}</td>}
                  {hasManifest && <td><DiffCell r={r} prev={list[i + 1]} /></td>}
                  <td className="acts">{r.id !== currentId && <button className="a-btn sm" disabled={busy != null} onClick={() => void rollback(r)}>{busy === r.id ? 'Visszaállítás…' : 'Visszaállítás erre'}</button>}</td>
                </tr>
              ))}
              {list.length === 0 && <tr className="empty"><td colSpan={hasManifest ? 6 : 4}>Még nem volt kiadás.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {!list && !err && <p className="a-sub">betöltés…</p>}
    </div>
  );
}
